import React from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Badge,
  Button,
} from "@glucose-care/shared";
import { Bell, BellRing } from "lucide-react";
import { Notification } from "../../contexts/NotificationContext";
import { sanitizeInput } from "../../utils/security";

interface NotificationDropdownProps {
  notifications: Notification[];
  unreadCount: number;
  onNotificationClick: (id: string) => void;
  onMarkAllAsRead: () => void;
  onViewAll: () => void;
  isActive: boolean;
  variant?: "desktop" | "mobile";
}

export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  notifications,
  unreadCount,
  onNotificationClick,
  onMarkAllAsRead,
  onViewAll,
  isActive,
  variant = "desktop",
}) => {
  const isMobile = variant === "mobile";

  const TriggerButton = isMobile ? (
    <Button
      variant="ghost"
      size="sm"
      className="relative hover:bg-gray-100 p-2 rounded-full"
      aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount})` : ""}`}
    >
      <Bell className="w-5 h-5" aria-hidden="true" />
      {unreadCount > 0 && (
        <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center bg-red-500 text-white text-xs rounded-full">
          {unreadCount > 9 ? "9+" : unreadCount}
        </Badge>
      )}
    </Button>
  ) : (
    // Desktop: styled to match a NavRow (soft mint active + teal edge accent).
    <button
      className={`group relative w-full flex items-center gap-3 px-3 h-11 rounded-xl text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#26B5AA]/40 ${
        isActive ? "bg-[#E6F7F4] text-[#1E9B95]" : "text-gray-700 hover:bg-gray-50"
      }`}
      aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
    >
      {isActive && (
        <span
          className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-1 rounded-r-full bg-[#26B5AA]"
          aria-hidden="true"
        />
      )}
      <Bell
        className={`w-5 h-5 flex-shrink-0 ${
          isActive ? "text-[#26B5AA]" : "text-gray-500 group-hover:text-[#26B5AA]"
        }`}
        aria-hidden="true"
      />
      <span className="flex-1 text-left truncate">Notifications</span>
      {unreadCount > 0 && (
        <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[#26B5AA] text-white text-xs font-semibold flex items-center justify-center">
          {unreadCount > 9 ? "9+" : unreadCount}
        </span>
      )}
    </button>
  );

  const maxItems = isMobile ? 3 : 5;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{TriggerButton}</DropdownMenuTrigger>
      <DropdownMenuContent className="w-80 bg-white border border-gray-200 shadow-lg rounded-xl">
        <DropdownMenuLabel className="text-sm font-semibold flex justify-between p-4 border-b border-gray-200">
          <span>Notifications</span>
          {unreadCount > 0 && (
            <button
              onClick={onMarkAllAsRead}
              className="text-xs text-[#26B5AA] hover:text-[#1E9B95] font-medium"
              aria-label="Mark all as read"
            >
              Mark all as read
            </button>
          )}
        </DropdownMenuLabel>

        {notifications.length === 0 ? (
          <div className="p-8 text-center text-gray-500 text-sm">
            <Bell className="w-8 h-8 mx-auto mb-2 text-gray-400" aria-hidden="true" />
            <p className="text-xs">No notifications</p>
          </div>
        ) : (
          <>
            <div className="max-h-80 overflow-y-auto">
              {notifications.slice(0, maxItems).map((notification) => (
                <DropdownMenuItem
                  key={notification.id}
                  className="p-4 cursor-pointer hover:bg-gray-50 flex items-start gap-3"
                  onClick={() => {
                    onNotificationClick(notification.id);
                    onViewAll();
                  }}
                >
                  <div
                    className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${
                      !notification.read ? "bg-[#26B5AA] animate-pulse" : "bg-gray-300"
                    }`}
                    aria-hidden="true"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {sanitizeInput(notification.title, 100)}
                    </p>
                    <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                      {sanitizeInput(notification.message, 200)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">{notification.time}</p>
                  </div>
                </DropdownMenuItem>
              ))}
            </div>
            <div className="p-3 border-t border-gray-200">
              <button
                onClick={onViewAll}
                className="w-full text-center text-sm text-[#26B5AA] hover:text-[#1E9B95] font-medium py-2 flex items-center justify-center gap-2"
                aria-label="View all notifications"
              >
                <BellRing className="w-4 h-4" aria-hidden="true" />
                View All
              </button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};